import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import {
  Grid,
  Stack,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  OutlinedInput,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { trakrefFontFamilyBold } from "../../../utils";
import { trakrefBlue } from "../../../color_utils";
import useChangePassword from "../../../hooks/useChangePassword";

import { CommonModal } from "../index";
import UserInfo from "../../../UserInfo";

const initialState = {
  value: "",
  isVisible: false,
};

const PasswordEyeIconButton = ({ showPassword, handleClickShowPassword }) => {
  return (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={handleClickShowPassword}
        onMouseDown={(e) => e.preventDefault()}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );
};

const ChangePasswordModal = ({ modalOpen, userId, onClose, onLogoutSuccess, isProfile }) => {
  const [currentPassword, setCurrentPassword] = useState(initialState);
  const [newPassword, setNewPassword] = useState(initialState);
  const [confirmPassword, setConfirmPassword] = useState(initialState);

  const { isLoading, passwordError, confirmPasswordError, currentPasswordError, resetStates, changePassword } =
    useChangePassword({
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
      confirmPassword: confirmPassword.value,
    });

    const onShowCurrentPasswordText = () => {
        setCurrentPassword((prev) => ({
            ...prev,
            isVisible: !prev.isVisible,
        }));
    };

  const onShowNewPasswordText = () => {
    setNewPassword((prev) => ({
      ...prev,
      isVisible: !prev.isVisible,
    }));
  };

  const onShowConfirmPasswordText = () => {
    setConfirmPassword((prev) => ({
      ...prev,
      isVisible: !prev.isVisible,
    }));
  };

  const onChangePassword = async () => {
    const state = await changePassword(userId);
    if (state) {
      onLogoutSuccess();
    }
  };

  useEffect(() => {
    if (!modalOpen) {
        resetStates();
        setCurrentPassword(initialState);
      setNewPassword(initialState);
      setConfirmPassword(initialState);
    }
  }, [modalOpen])

  return (
    <CommonModal open={modalOpen} close={onClose} header="Change Password">
      <Grid container pl={10} pr={10}>
        <div style={{ textAlign: "left" }}>
          <span
            style={{
              fontSize: "13px",
              lineHeight: "1.2",
              display: "inline-block",
            }}
          >
            In order to protect your account, make sure you choose a unique
            password that:
          </span>
          <ul
            style={{ textAlign: "left", paddingLeft: "15px", fontSize: "13px" }}
          >
            <li>Have at least 8 characters </li>
            <li>Have at least 1 capital letter </li>
            <li>Have at least 1 special character </li>
          </ul>
        </div>
        <Grid item xs={12} lg={12} mb={2} pt={2} align="center">
          <Stack spacing={3}>
            <FormControl
              error={!!currentPasswordError}
              variant="outlined"
              sx={{
                "&  .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
                  color: "#D51A1A !important",
                },
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D51A1A !important",
                },
              }}
              >
            <InputLabel htmlFor="CurrentPassword">{isProfile ? "Enter current password" : "Enter your current password"}</InputLabel>
             <OutlinedInput
                Id="CurrentPassword"
                aria-describedby="CurrentPassword-error-text"
                label={isProfile ? "Enter current password" : "Enter your current password"}
                fullWidth
                value={currentPassword.value}
                onChange={(e) =>
                    setCurrentPassword((prev) => ({ ...prev, value: e.target.value }))
                }
                type={currentPassword.isVisible ? "text" : "password"}
                endAdornment={
                    <PasswordEyeIconButton
                        showPassword={currentPassword.isVisible}
                        handleClickShowPassword={onShowCurrentPasswordText}
                    />
                }
            />
            <FormHelperText id="NewPassword-error-text">
                {currentPasswordError}
            </FormHelperText>
            </FormControl>
            <FormControl
                error={!!passwordError}
                variant="outlined"
                sx={{
                    "&  .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
                        color: "#D51A1A !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#D51A1A !important",
                    },
                }}
            >

            <InputLabel htmlFor="NewPassword">{isProfile ? "Enter new password" : "Enter user's new password"}</InputLabel>
                <OutlinedInput
                    Id="NewPassword"
                    aria-describedby="NewPassword-error-text"
                    label={isProfile ? "Enter new password" : "Enter user's new password"}
                    fullWidth
                    value={newPassword.value}
                    onChange={(e) =>
                        setNewPassword((prev) => ({ ...prev, value: e.target.value }))
                    }
                    type={newPassword.isVisible ? "text" : "password"}
                    endAdornment={
                        <PasswordEyeIconButton
                            showPassword={newPassword.isVisible}
                            handleClickShowPassword={onShowNewPasswordText}
                        />
                    }
                />
                <FormHelperText id="NewPassword-error-text">
                    {passwordError}
                </FormHelperText>
            </FormControl>
            <FormControl
              error={!!confirmPasswordError}
              variant="outlined"
              sx={{
                "&  .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
                  color: "#D51A1A !important",
                },
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D51A1A !important",
                },
              }}
            >
              <InputLabel htmlFor="ConfirmPassword">
                    {isProfile ? "Confirm new password" : "Confirm user's new password"}
              </InputLabel>
              <OutlinedInput
                Id="ConfirmPassword"
                label={isProfile ? "Confirm new password" : "Confirm user's new password"}
                fullWidth
                value={confirmPassword.value}
                onChange={(e) =>
                  setConfirmPassword((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }))
                }
                type={confirmPassword.isVisible ? "text" : "password"}
                endAdornment={
                  <PasswordEyeIconButton
                    showPassword={confirmPassword.isVisible}
                    handleClickShowPassword={onShowConfirmPasswordText}
                  />
                }
              />
              <FormHelperText id="NewPassword-error-text">
                {confirmPasswordError}
              </FormHelperText>
            </FormControl>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={12} align="center">
          <Box mt={2} mb={2} sx={{ width: "100%" }}>
            <LoadingButton
              fullWidth
              size="large"
              variant="contained"
              loading={isLoading}
              onClick={onChangePassword}
              sx={{
                "& .MuiCircularProgress-root": {
                  color: "#FFFFFF",
                  width: "21px !important",
                  height: "21px !important",
                },
              }}
              style={{
                backgroundColor: trakrefBlue,
              }}
            >
              <Typography
                sx={{
                  fontFamily: trakrefFontFamilyBold,
                  backgroundColor: trakrefBlue,
                  letterSpacing: "1.2px",
                  padding: "1% 0%",
                  color: isLoading ? "transparent" : "#fff",
                  fontSize: 17,
                  fontWeight: 400,
                }}
              >
                Change Password
              </Typography>
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    </CommonModal>
  );
};

ChangePasswordModal.propTypes = {
  modalOpen: PropTypes.bool,
  userId: PropTypes.string,
  onClose: PropTypes.func,
  onLogoutSuccess: PropTypes.func,
  isProfile: PropTypes.bool
};

ChangePasswordModal.defaultProps = {
  modalOpen: false,
  userId: UserInfo.userId,
  onClose: () => {},
  onLogoutSuccess: () => { },
  isProfile: false
};

export default ChangePasswordModal;
